import React, { useState, useEffect } from 'react';
import { Prompt } from 'react-router-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { sorts, throttle, publisherOption, publisherValue } from '../../utils';
import { default as grade_levels } from '../../grade-levels.json';
import { default as languages } from '../../languages.json';
import { useFormik } from 'formik';
import { object, array, string, number, date } from 'yup';
import { format } from 'date-fns';
import Input from '../../components/form/FormikInput';
import Select from '../../components/form/FormikSelect';
import TextArea from '../../components/form/FormikTextArea';
import WysiwygEditor from '../../components/form/FormikWysiwygEditor';
import { ListInputBox, ListInputItem } from '../../components/form/FormikListInputBox';
import ImageUploader from '../../components/form/ImageUploader';
import ProgressBar from '../../components/ProgressBar';
import AsyncSelect from 'react-select/lib/Async';
import { Link } from 'react-router-dom';
import { addFile, deleteAsset } from './Assets';
import { StatusButton } from '../UserAdd';
import Modal from '../../components/Modal';
import { toast } from 'react-toastify';
import { saveTitleById, titleUpdate } from '../../slices/titleSlice';
import DOMPurify from 'isomorphic-dompurify';
import { runtimeRegex } from '../../utils';

const _debounce = require('lodash.debounce');

const MetadataReadOnly = React.lazy(() => import('./MetadataReadOnly'));

export const WhatsThis = ({ message }) => (
    <a href="#help" data-rh={message} onClick={(e) => e.preventDefault()}>
        <small>What's This</small>
    </a>
);

export const sanitizeTitle = (data, omitKeys = ['assets', 'asset_plan', '_modified', 'has_snapshot']) => {
    return Object.keys(data).reduce((obj, key) => {
        if (omitKeys.includes(key)) return obj;
        obj[key] = data[key];
        return obj;
    }, {});
};

export const getBisacs = (params) => {
    return axios.request({
        url: `${process.env.REACT_APP_API_URL}/unauthorized/bisac`,
        method: 'GET',
        params
    });
};

const territoriesList =
    'AD,AE,AF,AG,AI,AL,AM,AN,AO,AQ,AR,AS,AT,AU,AW,AX,AZ,BA,BB,BD,BE,BF,BG,BH,BI,BJ,BL,BM,BN,BO,BQ,BR,BS,BT,BV,BW,BY,BZ,CA,CC,CD,CF,CG,CH,CI,CK,CL,CM,CN,CO,CR,CS,CU,CV,CW,CX,CY,CZ,DE,DJ,DK,DM,DO,DZ,EC,EE,EG,EH,ER,ES,ET,FI,FJ,FK,FM,FO,FR,GA,GB,GD,GE,GF,GG,GH,GI,GL,GM,GN,GP,GQ,GR,GS,GT,GU,GW,GY,HK,HM,HN,HR,HT,HU,ID,IE,IL,IM,IN,IO,IQ,IR,IS,IT,JE,JM,JO,JP,KE,KG,KH,KI,KM,KN,KP,KR,KW,KY,KZ,LA,LB,LC,LI,LK,LR,LS,LT,LU,LV,LY,MA,MC,MD,ME,MF,MG,MH,MK,ML,MM,MN,MO,MP,MQ,MR,MS,MT,MU,MV,MW,MX,MY,MZ,NA,NC,NE,NF,NG,NI,NL,NO,NP,NR,NU,NZ,OM,PA,PE,PF,PG,PH,PK,PL,PM,PN,PR,PS,PT,PW,PY,QA,RE,RO,RS,RU,RW,SA,SB,SC,SD,SE,SG,SH,SI,SJ,SK,SL,SM,SN,SO,SR,SS,ST,SV,SX,SY,SZ,TC,TD,TF,TG,TH,TJ,TK,TL,TM,TN,TO,TR,TT,TV,TW,TZ,UA,UG,UM,US,UY,UZ,VA,VC,VE,VG,VI,VN,VU,WF,WS,YE,YT,YU,ZA,ZM,ZW';

const validateNamesList = (value) => {
    let passes = true;
    value.forEach((authorName) => {
        if (authorName.length > 200) {
            passes = false;
        }
    });

    if (value.join('').length > 1000) {
        passes = false;
    }

    return passes;
};

const validateTerritoryCodes = (value) => {
    let isCustomValid = true;

    if (value === 'WORLD') {
        return true;
    }

    value
        .replace(/\s/g, '')
        .split(',')
        .forEach((terr) => {
            if (!territoriesList.split(',').includes(terr)) {
                isCustomValid = false;
            }
        });

    return isCustomValid;
};

export const fictionTypes = ['Fiction', 'Non-Fiction'];

const dateValidation = date()
    .required('Required')
    .typeError('Invalid date format')
    .min(new Date('0001-01-01'), 'Date must be between 0001-01-01 and 2100-12-31')
    .max(new Date('2100-12-31'), 'Date must be between 0001-01-01 and 2100-12-31');

const typeErrorMessage = 'Please choose a value';

export const projectSchema = object().shape({
    title: string().min(1).max(175).required('Required'),
    subtitle: string()
        .min(1)
        .max(256 - 175)
        .nullable(),
    series: string().nullable().max(195),
    series_number: number().nullable().typeError('Series Number must be a number (1 or 1.0)').max(99999),
    description: string()
        .max(2000, 'Description must be less than 2,000 characters.  Note that styling can impact your character count')
        .required('Required')
        .typeError('Please complete the description field'),
    publisher: string().required('Required').nullable(),
    imprint: string().nullable(),
    authors: array()
        .default([])
        .required('Required')
        .test('author-length', 'Author names cannot exceed 200 characters (1000 characters total)', validateNamesList),
    narrators: array()
        .default([])
        .required('Required')
        .test(
            'narrator-length',
            'Narrator names cannot exceed 200 characters (1000 characters total)',
            validateNamesList
        ),
    translator: string().nullable(),

    abridgement: string().required('Required').typeError(typeErrorMessage),
    language: string().required('Required').typeError(typeErrorMessage),
    runtime: string().nullable().matches(runtimeRegex, 'Runtime should be in the format HH:MM:SS'),

    fiction_type: string().required('Required').nullable(),
    grade_level: string().required('Required').nullable(),
    bisac_codes: array(string()).required('Required').nullable(),

    isbn_retail: string().max(13).nullable(),
    isbn_ebook_retail: string().max(13).nullable(),
    isbn_print_retail: string().max(13).nullable(),
    isbn_library: string().max(13).nullable(),
    isbn_ebook_library: string().max(13).nullable(),
    isbn_print_library: string().max(13).nullable(),
    publisher_id: string().nullable(),

    price_retail: number().nullable().typeError('Retail Price must be a number (1 or 1.0)').required('Required'),
    price_library: number().nullable().typeError('Library Price must be a number (1 or 1.0)'),
    currency: string().default('USD').required('Required'),
    // per_circ_price: number().default(0.99).typeError('Pay per Circ price must be a number (1 or 1.0)'),
    audio_copyright_year: number()
        .nullable()
        .max(9999)
        .typeError('Must be a valid year. ex. 1984')
        .required('Required'),
    audio_copyright_owner: string().nullable().required('Required'),
    source_copyright_year: number()
        .nullable()
        .max(9999)
        .typeError('Must be a valid year. ex. 1984')
        .required('Required'),
    source_copyright_owner: string().nullable().required('Required'),
    custom_territories: string().when('territory_type', {
        is: (value) => value === 'Custom',
        then: string().test('valid-territories', 'Please enter all valid territory codes.', validateTerritoryCodes)
    }),
    original_release_date: dateValidation,
    strict_onsale_date: date().when('original_release_date', {
        is: (value) => format(value) > format(new Date()),
        then: dateValidation,
        otherwise: date().typeError('Invalid date format')
    })
});

export const TitleMetadata = (props) => {
    const dispatch = useDispatch();
    const { isLoading, data } = useSelector((state) => state.title);
    const session = useSelector((state) => state.session);
    let [success, setSuccess] = useState();
    let [imageProgress, setImageProgress] = useState(0);
    let [imageSize, setImageSize] = useState(0);
    let [imageError, setImageError] = useState(null);
    let [showSubmitModal, setShowSubmitModal] = useState(false);
    let [bisacTemp, setBisacTemp] = useState('');
    let [warnings, setWarnings] = useState({});
    let [pubs, setPubs] = useState(session?.user?.provider?.publishers || []);

    const scrollToErrors = (errors) => {
        const errorKeys = Object.keys(errors);
        if (errorKeys.length > 0) {
            let scrollToEl = document.getElementById(errorKeys[0]);
            if (scrollToEl) {
                scrollToEl.scrollIntoView({ behavior: 'smooth' });
                scrollToEl.focus();
            }
        }
    };
    let handleSave = async (values, { setSubmitting }) => {
        try {
            setSubmitting(true);
            let _territories = 'WORLD';

            if (values.territory_type === 'Custom') {
                if (values.custom_territories instanceof Array) {
                    _territories = values.custom_territories;
                } else {
                    _territories = values.custom_territories
                        .split(',')
                        .map((t) => t.trim())
                        .join(',');
                }
            }

            let new_data = {
                ...values,
                bisac_codes: (values.bisac_codes || []).filter((b) => b).map((b) => b.code),
                description: DOMPurify.sanitize(values.description || ''),
                territories_retail: _territories,
                territories_library: _territories,
                vendorid: session.selectedVendor
            };
            let newTitle = await saveTitleById(data.id, new_data);
            dispatch(titleUpdate(newTitle));
            setSuccess(true);
            setShowSubmitModal(true);
            setTimeout(() => {
                setSubmitting(false);
                setSuccess(null);
            }, 3000);
            touchedRef.current = {};
            setErrors({});
            setTouched({});
        } catch (error) {
            console.error(error);
            toast.error('There was an error saving the title');
            let errorMessage = 'There was an error saving the title';
            const fieldErrors = {};
            if (error.response && error.response.data) {
                const errorDetails = error.response.data;
                for (const [key, value] of Object.entries(errorDetails)) {
                    fieldErrors[key] = value.join(', ');
                }
                if (Object.keys(fieldErrors).length > 0) {
                    errorMessage = `Please address the following errors to save the title: ${Object.values(fieldErrors).join(', ')}`;
                }
            }
            setErrors({ _error: errorMessage, ...fieldErrors });
        }
    };

    let handleImagePreDrop = () => {
        setImageProgress(0);
        setImageSize(0);
        setImageError(null);
    };

    let handleImageChange = async (file, url) => {
        // Note: If replacing an existing image, the backend will delete it once a new image is uploaded.
        setImageError(null);
        setImageSize(file.size);

        try {
            let resp = await addFile(
                data.id,
                file,
                {
                    assettype: 'image'
                },
                throttle((event) => {
                    setImageProgress(event.loaded);
                }, 500)
            );

            let values = Object.values(resp);

            setImageProgress(0);
            setImageSize(0);

            let assets = (data.assets || []).filter((asset) => asset.assettype !== 'image');
            dispatch(titleUpdate({ assets: [...assets, values[0].model] }));
        } catch (err) {
            console.error(err);
            toast.error('There was an error changing the image');
        }
    };

    let handleImageRemove = async () => {
        let image = data.assets.filter((a) => a.assettype === 'image')[0];
        await deleteAsset(image.id);

        setImageProgress(0);
        setImageSize(0);
        dispatch(titleUpdate({ assets: data.assets.filter((a) => a.id !== image.id) }));
    };

    let handleImageError = (err) => {
        setImageProgress(0);
        setImageSize(0);
        setImageError(err.message);
    };

    let throttledSearchBisac = _debounce((inputValue, values, callback) => {
        const { grade_level, fiction_type } = values;
        return getBisacs({
            q: inputValue,
            safe: grade_level && grade_level !== 'Adult',
            age_level: grade_level && grade_level.toLowerCase(),
            fiction_type: fiction_type && fiction_type.toLowerCase()
        })
            .then((resp) => {
                callback(
                    resp.data.results.map((v) => ({ label: v.literal, value: v.code, orig: v })),
                    null
                );
            })
            .catch((err) => callback(err, null));
    }, 350);

    let searchBisac = (inputValue, values, callback) => {
        if (!inputValue || inputValue.length === 1) {
            return Promise.resolve([]);
        }
        throttledSearchBisac(inputValue, values, callback);
    };

    let handleBisacSelect = (value, values, callback) => {
        if (!value) return;
        callback('bisac_codes', [...(values.bisac_codes || []), value.orig]);
    };

    let removeBisac = (code, values, callback) => {
        callback(
            'bisac_codes',
            values.bisac_codes.filter((c) => c.code !== code)
        );
    };

    const handleCustomTerritoriesChange = (event, values, callback) => {
        let _value = event.target.value;
        setFieldValue('territories_retail', _value);
        setFieldValue('custom_territories', _value);
    };

    let origData = data;
    let image = (data.assets || []).filter((a) => a.assettype === 'image')[0];

    let {
        values,
        errors,
        touched,
        isSubmitting,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        setTouched,
        setErrors
    } = useFormik({
        initialValues: sanitizeTitle({
            ...data,
            series: data?.series || '',
            series_number: data?.series_number || '',
            keywords: data?.keywords || [],
            currency: 'USD',
            grade_level: data?.grade_level === 'Pre - K' ? 'Pre-K' : data.grade_level,
            custom_territories: territoriesList
            // per_circ_price: data.per_circ_price === null ? 0.99 : data.per_circ_price
        }),
        enableReinitialize: true,
        validationSchema: projectSchema,
        onSubmit: handleSave
    });

    let hasSeriesInfo = values.series || values.series_number;
    let showSeriesWarning = (!values.series || !values.series_number) && hasSeriesInfo;

    const touchedRef = React.useRef(touched);

    React.useEffect(() => {
        touchedRef.current = touched;
    }, [touched]);

    React.useEffect(() => {
        window.addEventListener('beforeunload', (e) => {
            if (Object.keys(touchedRef.current).length) {
                e.returnValue = '';
                return '';
            } else {
                delete e['returnValue'];
                return undefined;
            }
        });
    }, []);

    React.useEffect(() => {
        if (data?.territories_retail?.includes('WORLD') || !data.territories_retail) {
            setFieldValue('territory_type', 'WORLD');
        } else if (data?.territories_retail?.length) {
            setFieldValue('territory_type', 'Custom');
            setFieldValue('custom_territories', data.territories_retail);
        }
    }, [data.territories_retail, props.match.params.catId, setFieldValue]);

    React.useEffect(() => {
        async function fetchPubs() {
            let res = await axios.request({
                url: `${process.env.REACT_APP_API_URL}/authorized/vendor/${session.selectedVendor}/publishers`,
                method: 'GET'
            });
            setPubs(res.data);
        }
        if (session.selectedVendor) {
            fetchPubs();
        }
    }, [session.selectedVendor]);

    // In certain cases Admins don't any set publisher if the user (admin) is not associated with that publisher.
    // This fixes that by adding the title's set publisher to the list of "pubs" generated from
    // the user (admin) provider's list of publishers.
    useEffect(() => {
        // pubs may hold {name, display_name} objects from the endpoint or, via this
        // fallback, a bare canonical string. Compare on the canonical name either way.
        if (!isLoading && data.publisher && !pubs.some((p) => publisherValue(p) === data.publisher)) {
            setPubs([...pubs, { name: data.publisher, display_name: data.publisher_display_name }]);
        }
    }, [isLoading, data, pubs]);

    const pubOptions = pubs.map(publisherOption).filter((o) => o);

    if (session.isReadOnly || !session.enableEditing) {
        return (
            <React.Suspense fallback={<div></div>}>
                <MetadataReadOnly title={data} session={session} />
            </React.Suspense>
        );
    }

    let SaveButton = (
        <StatusButton
            onClick={() => scrollToErrors(errors)}
            label="Save All Changes"
            disabled={isSubmitting}
            successLabel="Changes Saved"
            success={success}
        />
    );

    let imageUrl = null;
    if (image) {
        imageUrl = image.signedurl || image.filename;
    }

    if (imageUrl && imageUrl.includes('images.inaudio.com')) {
        imageUrl = `${imageUrl}?aspect=1:1`;
    }

    if (image && image.filename.includes('newimages.inaudio.com')) {
        imageUrl = `//images.inaudio.com/v1/image/cover/${data.sku}?aspect=1:1`;
    }

    const checkForCommas = (values, field) => {
        let hasCommas = false;

        (values || []).forEach((val) => {
            if (val.includes(',')) {
                hasCommas = true;
            }
        });

        if (hasCommas) {
            setWarnings({
                ...warnings,
                [field]:
                    'At least one name includes a comma. If you meant to include a list of names, please add them one at a time.'
            });
        } else {
            setWarnings({
                ...warnings,
                [field]: ''
            });
        }
    };

    return (
        <div className="page-metadata">
            <p>
                Below is the metadata and artwork associated with this title. To add new data or make changes to the
                existing data, edit the fields below and be sure to save your work. If your audiobook or metadata
                references supplemental materials, please submit PDF files named by audiobook ISBN{' '}
                <a
                    href="https://support.passport.inaudio.com/support/tickets/new"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    as a ticket on our support portal
                </a>
                .
            </p>
            <p>
                Please ensure your audio, metadata, and cover art meet{' '}
                <a href={process.env.REACT_APP_TECH_SPECS_DOC} target="_blank" rel="noopener noreferrer">
                    these specifications
                </a>{' '}
                to ensure that your title is made available at all distributors.
            </p>
            <p>
                Please note that changes will not be made available to INaudio's Partner network until you{' '}
                <Link to={`/catalog/${data.id}/submit`}>Review & Submit</Link> all changes
            </p>
            {!isLoading && data && (
                <form action="" className="mb-3 row" onSubmit={handleSubmit}>
                    <div className="col-xs-12">
                        <div className="card mb-4">
                            <div className="row">
                                <div className="image-uploader-wrapper">
                                    <ImageUploader
                                        className="mb-3"
                                        replaceable={data.status === 'Drafted' ? false : true}
                                        onChange={handleImageChange}
                                        onRemove={handleImageRemove}
                                        onError={handleImageError}
                                        onPreDrop={handleImagePreDrop}
                                        filename={imageUrl}
                                        uploadText="Click or drag to upload artwork. We prefer PNGs that are 3000px by 3000px."
                                        disabled={!!imageSize}
                                    />
                                    {!!imageError && <div className="flash-error mb-3">{imageError}</div>}
                                    {!!imageSize && <ProgressBar current={imageProgress} total={imageSize} />}
                                </div>
                                <div className="col-xs-12 col-md">
                                    <Input
                                        label="Title"
                                        id="title"
                                        value={values.title || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        showRequired
                                        min={1}
                                        max={175}
                                    />
                                    <Input
                                        label="Subtitle"
                                        id="subtitle"
                                        value={values.subtitle || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        errors={errors.subtitle}
                                        touched={touched.subtitle}
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-xs-12 col-md-9">
                                    <Input
                                        label="Series Name"
                                        labelHelp={
                                            <WhatsThis message="Some retailers use this value to group all the audiobooks in your series together. Make sure it matches the same values you enter for all the other audiobooks in this series." />
                                        }
                                        id="series"
                                        value={values.series || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        errors={errors.series}
                                        touched={touched.series}
                                        collapse={showSeriesWarning}
                                    />
                                </div>
                                <div className="col-xs-12 col-md-3">
                                    <Input
                                        label="Number in Series"
                                        id="series_number"
                                        value={values.series_number || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        errors={errors.series_number}
                                        touched={touched.series_number}
                                        collapse={showSeriesWarning}
                                    />
                                </div>
                            </div>

                            {showSeriesWarning && (
                                <div className="flash flash-notice mt-2 mb-4">
                                    Most books have both a series name and series number - one of those fields is blank.
                                </div>
                            )}

                            <div id="description">
                                <WysiwygEditor
                                    label="Description"
                                    id="description"
                                    showRequired
                                    html={values.description || ''}
                                    placeholder="Tell listeners what your book is about! (Hint: this is the copy you would normally find on the back of a book)"
                                    onChange={setFieldValue}
                                    onBlur={setFieldTouched}
                                    errors={errors.description}
                                    touched={touched.description}
                                />
                            </div>
                        </div>

                        <div className="card card-flat mb-4">
                            <div className="card-header">
                                <h3>Contributors</h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-xs-12 col-md-6">
                                        <Select
                                            id="publisher"
                                            label="Publisher"
                                            value={values.publisher || ''}
                                            showRequired
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.publisher}
                                            touched={touched.publisher}
                                            disabled={origData.publisher && data.status !== 'Drafted'}
                                        >
                                            <option value="">Select...</option>
                                            {/* Publisher rename (T10): `value` is the
                                                canonical name and is what gets submitted
                                                and stored in tblTitle.publisher. Only the
                                                label shows the rebranded name. */}
                                            {pubOptions.map((pub) => (
                                                <option key={pub.value} value={pub.value}>
                                                    {pub.label}
                                                </option>
                                            ))}
                                        </Select>
                                    </div>
                                    <div className="col-xs-12 col-md-6">
                                        <Input
                                            type="text"
                                            id="imprint"
                                            label="Imprint"
                                            value={values.imprint || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.imprint}
                                            touched={touched.imprint}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-12 col-md-6">
                                        <ListInputBox
                                            value={values.authors || []}
                                            id="authors"
                                            label="Authors"
                                            onChange={(values) => {
                                                setFieldValue('authors', values);
                                                checkForCommas(values, 'authors');
                                            }}
                                            onBlur={setFieldTouched}
                                            errors={errors.authors}
                                            warnings={warnings.authors}
                                            touched={touched.authors}
                                            showRequired
                                            help="Type author name and click 'Add'"
                                        >
                                            {({ removeItem, items }) => (
                                                <ul className="list-group list-group-alt">
                                                    {items.map((item, index) => (
                                                        <ListInputItem key={item} index={index} onRemove={removeItem}>
                                                            {item}
                                                        </ListInputItem>
                                                    ))}
                                                </ul>
                                            )}
                                        </ListInputBox>
                                    </div>
                                    <div className="col-xs-12 col-md-6">
                                        <ListInputBox
                                            value={values.narrators || []}
                                            id="narrators"
                                            label="Narrators"
                                            onChange={(values) => {
                                                setFieldValue('narrators', values);
                                                checkForCommas(values, 'narrators');
                                            }}
                                            onBlur={setFieldTouched}
                                            errors={errors.narrators}
                                            warnings={warnings.narrators}
                                            touched={touched.narrators}
                                            showRequired
                                            help="Type narrator name and click 'Add'"
                                        >
                                            {({ removeItem, items }) => (
                                                <ul className="list-group list-group-alt">
                                                    {items.map((item, index) => (
                                                        <ListInputItem key={item} index={index} onRemove={removeItem}>
                                                            {item}
                                                        </ListInputItem>
                                                    ))}
                                                </ul>
                                            )}
                                        </ListInputBox>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xs-12 col-md-6">
                                        <Input
                                            value={values.translator || []}
                                            id="translator"
                                            label="Translator"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.translator}
                                            touched={touched.translator}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card card-flat mb-4">
                            <div className="card-header">
                                <h3>Additional Metadata</h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-xs-12 col-md-6">
                                        <Select
                                            id="abridgement"
                                            label="Abridgement Status"
                                            value={values.abridgement || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.abridgement}
                                            touched={touched.abridgement}
                                            defaultEmpty
                                            showRequired
                                        >
                                            <option value="Abridged">Abridged</option>
                                            <option value="Unabridged">Unabridged</option>
                                        </Select>
                                    </div>
                                    <div className="col-xs-12 col-md-6">
                                        <Input
                                            label="Approximate Runtime"
                                            id="runtime"
                                            value={values.runtime || ''}
                                            placeholder="HH:MM:SS"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.runtime}
                                            touched={touched.runtime}
                                            labelHelp={
                                                <WhatsThis message="This value is meant to be informational, INaudio will measure the actual audio duration of your uploaded files" />
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-12 col-md-6">
                                        <Select
                                            id="language"
                                            label="Language"
                                            value={values.language || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.language}
                                            touched={touched.language}
                                            showRequired
                                        >
                                            <option value="">Select</option>
                                            {(languages || []).sort().map((l) => (
                                                <option key={l} value={l}>
                                                    {l}
                                                </option>
                                            ))}
                                        </Select>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xs-12 col-md-6">
                                        <div className="label-wrapper mb-2">
                                            <label>
                                                Territories{' '}
                                                <small className="font-weight-normal font-italic text-danger">
                                                    (required)
                                                </small>
                                            </label>
                                            <a
                                                href="#help"
                                                data-rh="The territory is any country you want your audiobook available for sale."
                                            >
                                                <small>What's This?</small>
                                            </a>
                                        </div>
                                        <div>
                                            <label>
                                                <input
                                                    id="WORLD"
                                                    type="radio"
                                                    name="territory_type"
                                                    value="WORLD"
                                                    checked={values.territory_type === 'WORLD'}
                                                    onChange={handleChange}
                                                />
                                                WORLD
                                            </label>
                                        </div>
                                        <div className="mb-2">
                                            <label>
                                                <input
                                                    id="Custom"
                                                    type="radio"
                                                    name="territory_type"
                                                    value="Custom"
                                                    checked={values.territory_type === 'Custom'}
                                                    onChange={handleChange}
                                                />
                                                Country Code List
                                            </label>
                                        </div>
                                        <div>
                                            {values.territory_type === 'Custom' && (
                                                <>
                                                    <small className="form-text text-muted d-block mt-0 mb-1">
                                                        Provide a comma-separated list of two-letter country codes.
                                                    </small>
                                                    <TextArea
                                                        id="custom_territories"
                                                        name="custom_territories"
                                                        value={values.custom_territories}
                                                        onChange={(event) =>
                                                            handleCustomTerritoriesChange(event, values)
                                                        }
                                                        onBlur={handleBlur}
                                                        touched={touched}
                                                        errors={errors.custom_territories}
                                                    />
                                                </>
                                            )}
                                        </div>
                                        {errors.territories_retail &&
                                            touched.territories_retail &&
                                            errors.territories_retail}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card card-flat mb-4">
                            <div className="card-header">
                                <h3>Classification</h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-xs-12 col-md-6">
                                        <Select
                                            id="fiction_type"
                                            label="Fiction/Non-Fiction"
                                            value={values.fiction_type || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.fiction_type}
                                            touched={touched.fiction_type}
                                            showRequired
                                        >
                                            <option value="">Select...</option>
                                            {fictionTypes.map((pub) => (
                                                <option key={pub} value={pub}>
                                                    {pub}
                                                </option>
                                            ))}
                                        </Select>
                                    </div>
                                    <div className="col-xs-12 col-md-6">
                                        <Select
                                            id="grade_level"
                                            label="Grade Level"
                                            labelHelp={
                                                <WhatsThis message="This is the the primary audience for your audiobook. Grade level relates to age range. As follows: (Pre-k = Ages 0-5, Grades K-3 = Ages 5-8, Grades 3-6 = Ages 8-12, Grades 5-8 = Ages 12-14, Young Adult = Ages 14-18, Adult = Ages 18+)" />
                                            }
                                            value={values.grade_level || ''}
                                            defaultEmpty
                                            showRequired
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.grade_level}
                                            touched={touched.grade_level}
                                        >
                                            {grade_levels.sort(sorts.asc).map((level) => (
                                                <option key={level} value={level}>
                                                    {level}
                                                </option>
                                            ))}
                                        </Select>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xs-12 col-md-6">
                                        <div className="label-wrapper">
                                            <label htmlFor="bisac_codes">
                                                BISAC(s){' '}
                                                <small className="font-weight-normal font-italic text-danger">
                                                    (required)
                                                </small>
                                            </label>
                                            <WhatsThis message="This is your audiobook's category or subgenre. Start typing about your audiobook to see a full list of category options. Have more than one subgenre? You can add up to three BISACs." />
                                        </div>
                                        <AsyncSelect
                                            className="mb-3"
                                            id="bisac_codes"
                                            cacheOptions
                                            isClearable
                                            inputValue={bisacTemp}
                                            value={bisacTemp}
                                            onInputChange={(v, action) => {
                                                if (action.action === 'input-change') {
                                                    setBisacTemp(v);
                                                }
                                            }}
                                            loadOptions={(v, cb) => searchBisac(v, values, cb)}
                                            onChange={(v) => handleBisacSelect(v, values, setFieldValue)}
                                            placeholder="Start Typing..."
                                        />
                                        <ul className="list-group list-group-alt">
                                            {(values.bisac_codes || []).map((item, index) => (
                                                <li key={item.code} className="list-group-item">
                                                    {item.literal}
                                                    <button
                                                        type="button"
                                                        className="close"
                                                        aria-label="Close"
                                                        onClick={() => removeBisac(item.code, values, setFieldValue)}
                                                    >
                                                        <span aria-hidden="true">&times;</span>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                        {errors.bisac_codes && touched.bisac_codes && (
                                            <div className="text-danger">{errors.bisac_codes}</div>
                                        )}
                                    </div>
                                    <div className="col-xs-12 col-md-6">
                                        <ListInputBox
                                            id="keywords"
                                            label="Keywords"
                                            restricted={[';', '%', '$']}
                                            placeholder=""
                                            value={values.keywords}
                                            charLimit={950}
                                            labelHelp={
                                                <WhatsThis message="Keywords are used by some audiobook retailers to inform search and recommendation functionality. Press “add” for each distinct Keyword you’d like sent to retailers." />
                                            }
                                            error={errors.keywords}
                                            touched={errors.keywords}
                                            onChange={(values) => {
                                                if (values.length && values[values.length - 1].indexOf(',' > -1)) {
                                                    let modifiedValues = [];
                                                    let splitValues = values[values.length - 1]
                                                        .split(',')
                                                        .map((item) => item.trim());
                                                    values.pop();
                                                    modifiedValues = [...values, ...splitValues];
                                                    setFieldValue('keywords', modifiedValues);
                                                } else {
                                                    setFieldValue('keywords', values);
                                                }
                                            }}
                                            help="Type a keyword and click 'Add'"
                                        >
                                            {({ items, removeItem }) => {
                                                return (
                                                    <ul className="list-group list-group-alt">
                                                        {items.map((item, index) => (
                                                            <ListInputItem
                                                                className="list-group"
                                                                index={index}
                                                                onRemove={removeItem}
                                                                key={`keywords_${index}`}
                                                            >
                                                                {item}
                                                            </ListInputItem>
                                                        ))}
                                                    </ul>
                                                );
                                            }}
                                        </ListInputBox>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card card-flat mb-4">
                            <div className="card-header">
                                <h3>ISBN/ID</h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-xs-12 col-md-6">
                                        <Input
                                            id="isbn_retail"
                                            label="Audiobook Retail ISBN"
                                            labelHelp={
                                                <WhatsThis message="If your audiobook has an existing retail ISBN, add it here." />
                                            }
                                            value={values.isbn_retail || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={
                                                origData.isbn_retail &&
                                                (origData.status === 'Complete' || origData.status === 'Submitted')
                                            }
                                            errors={errors.isbn_retail}
                                            touched={touched.isbn_retail}
                                        />
                                        <div className="row">
                                            <div className="col-xs-12 col-md-6">
                                                <Input
                                                    id="isbn_ebook_retail"
                                                    label="eBook ISBN (retail)"
                                                    labelHelp={
                                                        <WhatsThis message="If you have an eBook edition of your audiobook, add the retail ISBN here." />
                                                    }
                                                    value={values.isbn_ebook_retail || ''}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    disabled={origData.isbn_ebook_retail}
                                                    errors={errors.isbn_ebook_retail}
                                                    touched={touched.isbn_ebook_retail}
                                                />
                                            </div>
                                            <div className="col-xs-12 col-md-6">
                                                <Input
                                                    id="isbn_print_retail"
                                                    label="Print ISBN (retail)"
                                                    labelHelp={
                                                        <WhatsThis message="If you have a print edition of your audiobook, add the retail ISBN here." />
                                                    }
                                                    value={values.isbn_print_retail || ''}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    disabled={origData.isbn_print_retail}
                                                    errors={errors.isbn_print_retail}
                                                    touched={touched.isbn_print_retail}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xs-12 col-md-6">
                                        <Input
                                            id="isbn_library"
                                            label="Audiobook Library ISBN"
                                            labelHelp={
                                                <WhatsThis message="If your audiobook has an existing library ISBN, add it here. Note that our library partners cannot sell your audiobook in the library channel without a Library ISBN." />
                                            }
                                            value={values.isbn_library || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={
                                                origData.isbn_library &&
                                                (origData.status === 'Complete' || origData.status === 'Submitted')
                                            }
                                            errors={errors.isbn_library}
                                            touched={touched.isbn_library}
                                        />

                                        <div className="row">
                                            <div className="col-xs-12 col-md-6">
                                                <Input
                                                    id="isbn_ebook_library"
                                                    label="eBook ISBN (library)"
                                                    labelHelp={
                                                        <WhatsThis message="If you have an eBook edition of your audiobook, add the library ISBN here." />
                                                    }
                                                    value={values.isbn_ebook_library || ''}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    disabled={origData.isbn_ebook_library}
                                                    errors={errors.isbn_ebook_library}
                                                    touched={touched.isbn_ebook_library}
                                                />
                                            </div>
                                            <div className="col-xs-12 col-md-6">
                                                <Input
                                                    id="isbn_print_library"
                                                    label="Print ISBN (library)"
                                                    labelHelp={
                                                        <WhatsThis message="If you have a print edition of your audiobook, add the library ISBN here." />
                                                    }
                                                    value={values.isbn_print_library || ''}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    disabled={origData.isbn_print_library}
                                                    errors={errors.isbn_print_library}
                                                    touched={touched.isbn_print_library}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card card-flat mb-4">
                            <div className="card-header">
                                <h3>Prices</h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-xs-12 col-sm-6">
                                        <Input
                                            id="price_retail"
                                            label="Retail List Price"
                                            labelHelp={
                                                <WhatsThis message="This is the price you would like to sell your audiobook for in retail channels." />
                                            }
                                            value={values.price_retail || ''}
                                            prepend="$"
                                            showRequired
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.price_retail}
                                            touched={touched.price_retail}
                                        />
                                    </div>
                                    <div className="col-xs-12 col-sm-6">
                                        <Input
                                            id="price_library"
                                            label="Library List Price"
                                            labelHelp={
                                                <WhatsThis message="This is the price of your content in the Library channel for the a la carte business model. Typically, the Library Price is 2-3 times higher than the Retail List Price." />
                                            }
                                            value={values.price_library || ''}
                                            prepend="$"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.price_library}
                                            touched={touched.price_library}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-12 col-sm-6">
                                        <Input
                                            label="Currency"
                                            id="currency"
                                            labelHelp={<WhatsThis message="INaudio only accepts pricing in USD." />}
                                            value={values.currency}
                                            errors={errors.currency}
                                            touched={touched.currency}
                                            showRequired
                                            disabled
                                        />
                                    </div>
                                    {/* <div className="col-xs-12 col-sm-6">
                                        <Input
                                            id="per_circ_price"
                                            label="Pay per Circ price"
                                            labelHelp={
                                                <WhatsThis message="Pay-per-circulation is a business model within the Library Channel. Each time a patron checks out a title, you earn a royalty. We recommend pricing at ¢.99 but you are free to choose your own price. " />
                                            }
                                            value={values.per_circ_price === null ? 0.99 : values.per_circ_price}
                                            prepend="$"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.per_circ_price}
                                            touched={touched.per_circ_price}
                                        />
                                    </div> */}
                                </div>
                            </div>
                        </div>

                        <div className="card card-flat mb-4">
                            <div className="card-header">
                                <h3>Dates/Copyright</h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-xs-12 col-md-6">
                                        <Input
                                            id="original_release_date"
                                            label="Original Release Date"
                                            labelHelp={
                                                <WhatsThis message="The Release Date is the first date that your audiobook was or will be officially available for purchase on any platform including those outside of INaudio." />
                                            }
                                            type="date"
                                            value={values.original_release_date || ''}
                                            showRequired
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.original_release_date}
                                            touched={touched.original_release_date}
                                        />
                                    </div>
                                    <div className="col-xs-12 col-md-6">
                                        <Input
                                            id="strict_onsale_date"
                                            label="Strict Onsale Date"
                                            labelHelp={
                                                <WhatsThis message="This is the date INaudio can officially make your audiobook available for sale." />
                                            }
                                            type="date"
                                            value={values.strict_onsale_date || ''}
                                            showRequired
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.strict_onsale_date}
                                            touched={touched.strict_onsale_date}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xs-12 col-lg-6">
                                        <Input
                                            id="audio_copyright_year"
                                            label="Audio Copyright Year"
                                            labelHelp={
                                                <WhatsThis message="This is the copyright year (4 digits) of this audiobook production." />
                                            }
                                            value={values.audio_copyright_year || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.audio_copyright_year}
                                            touched={touched.audio_copyright_year}
                                        />
                                    </div>
                                    <div className="col-xs-12 col-lg-6">
                                        <Input
                                            id="audio_copyright_owner"
                                            label="Audio Copyright Owner"
                                            labelHelp={
                                                <WhatsThis message="This is the copyright credit for this particular audio production. Do not include © symbol." />
                                            }
                                            value={values.audio_copyright_owner || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.audio_copyright_owner}
                                            touched={touched.audio_copyright_owner}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-12 col-lg-6">
                                        <Input
                                            id="source_copyright_year"
                                            label="Source Copyright Year"
                                            labelHelp={
                                                <WhatsThis message="This is the copyright year of the underlying source material for the audiobook production." />
                                            }
                                            value={values.source_copyright_year || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.source_copyright_year}
                                            touched={touched.source_copyright_year}
                                        />
                                    </div>
                                    <div className="col-xs-12 col-lg-6">
                                        <Input
                                            id="source_copyright_owner"
                                            label="Source Copyright Owner"
                                            labelHelp={
                                                <WhatsThis message="This is the copyright credit for the underlying source material for the audiobook production. Do not include (P) symbol." />
                                            }
                                            value={values.source_copyright_owner || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errors={errors.source_copyright_owner}
                                            touched={touched.source_copyright_owner}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card text-center">{SaveButton}</div>
                        {errors._error && <div className="flash-error text-center">{errors._error}</div>}
                    </div>
                </form>
            )}

            <Modal
                visible={showSubmitModal}
                onClose={(e) => {
                    e.preventDefault();
                    setShowSubmitModal(false);
                }}
            >
                <p className="text-center">
                    Your changes have been saved in Passport. Note that your changes will not go live in INaudio's
                    partner network until you review and submit all changes here.
                </p>
                {!image && (
                    <p className="text-center mb-4">
                        Cover Image is required prior to publishing your audiobook. Please upload an image before
                        continuing to Review & Submit.
                    </p>
                )}

                <div className="text-center">
                    <Link className="btn btn-primary" to={`/catalog/${data.id}/submit`}>
                        Review & Submit
                    </Link>
                    <button
                        className="btn btn-link"
                        onClick={(e) => {
                            e.preventDefault();
                            setShowSubmitModal(false);
                        }}
                    >
                        Close
                    </button>
                </div>
            </Modal>
            <Prompt
                when={!!Object.keys(touchedRef.current).length}
                message="Are you sure you want to leave the page? Changes you've made may not be saved."
            />
        </div>
    );
};

export default TitleMetadata;
